import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import SchemaDetailContext, {
  PendingCreations,
} from '@atom/components/schemaDetail/SchemaDetailContext';
import {
  ATTRIBUTE_CREATE_BULK,
  ATTRIBUTE_GROUP_UPDATE_BULK,
  ATTRIBUTE_UPDATE_BULK,
  ELEMENT_UPDATE,
  SCHEMA_CONVERSION_UNIT_CREATE_BULK,
  SCHEMA_CONVERSION_UNIT_UPDATE_BULK,
  SCHEMA_UPDATE,
} from '@atom/graph/schema';
import { Button, Icon, Menu, Modal, Snackbar } from '@atom/mui';
import {
  ArrowDropDownIcon,
  CheckIcon,
  EditIcon,
  LockIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  AttributeCreateBulkInput,
  AttributeGroupUpdateBulkInput,
  AttributeUpdateBulkInput,
  AttributeUpdateBulkInputItem,
  ElementUpdateInput,
} from '@atom/types/schema';
import {
  getErrorCode,
  isNilOrEmpty,
} from '@atom/utilities/validationUtilities';

import { getAttributeGroupsPayload } from '../schemaBuilder/subItemDetail/subItemDetailUtilities';
import { getConversionUnitBulkInputs } from '../schemaPreview/costDetail/costDetailUtilities';

import { getModalValue, ModalVariant } from './SchemaStatusModalValues';

import classes from './schemaDetailHeader.module.css';

const { MenuItem } = Menu;

const HTTP_STATUS_CONFLICT = 422;

const styles = {
  publishButton: {
    backgroundColor: colors.neutral.white,
    color: colors.brand.purple,
  },
};

const SchemaStatus = () => {
  const {
    schemaTree,
    refetchSchemaTree,
    pendingUpdates,
    setPendingUpdates,
    pendingCreations,
    setPendingCreations,
    pendingGroupUpdates,
    setPendingGroupUpdates,
    pendingSchemaUpdates,
    setPendingSchemaUpdates,
    pendingConversionUnits,
    setPendingConversionUnits,
  } = useContext(SchemaDetailContext);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [modalVariant, setModalVariant] = useState<ModalVariant>();

  const [updateSchema] = useMutation(SCHEMA_UPDATE);

  const [attributeUpdateBulk] = useMutation(ATTRIBUTE_UPDATE_BULK);

  const [attributeCreateBulk] = useMutation(ATTRIBUTE_CREATE_BULK);

  const [attributeGroupUpdateBulk] = useMutation(ATTRIBUTE_GROUP_UPDATE_BULK);

  const [updateElement] = useMutation(ELEMENT_UPDATE);

  const [conversionUnitCreateBulk] = useMutation(
    SCHEMA_CONVERSION_UNIT_CREATE_BULK,
  );

  const [conversionUnitUpdateBulk] = useMutation(
    SCHEMA_CONVERSION_UNIT_UPDATE_BULK,
  );

  const handleSchemaUpdate = async () => {
    setDisabled(true);
    const isPublished = modalVariant === ModalVariant.PUBLISH_CONFIRM;

    try {
      await updateSchema({
        variables: {
          input: {
            schemaId: schemaTree?.id,
            isPublished,
          },
        },
      });

      refetchSchemaTree();

      setModalVariant(null);
      setDisabled(false);
    } catch (err) {
      if (getErrorCode(err) === HTTP_STATUS_CONFLICT) {
        setModalVariant(ModalVariant.DRAFT_ERROR);
      } else {
        Snackbar.error({ message: 'An unknown error occurred' });
      }
      setDisabled(false);
    }
  };

  const publishPendingChanges = async (input: AttributeUpdateBulkInput) => {
    await attributeUpdateBulk({
      variables: {
        input,
      },
    });
  };

  const publishedPendingGroupChanges = async (
    input: AttributeGroupUpdateBulkInput,
  ) => {
    await attributeGroupUpdateBulk({
      variables: {
        input,
      },
    });
  };

  const publishPendingCreations = async (input: AttributeCreateBulkInput) => {
    await attributeCreateBulk({
      variables: {
        input,
      },
    });
  };

  const publishPendingSchemaChanges = async (input: ElementUpdateInput) => {
    await updateElement({
      variables: {
        input,
      },
    });
  };

  // Conversion units live on the root schema. Creates go first so an
  // isDefault: true sent in the update wins, since the server clears the
  // previous default whenever a unit is sent as the default.
  const publishPendingConversionUnits = async () => {
    const schemaId = schemaTree?.id;
    const { creates, updates } = getConversionUnitBulkInputs(
      pendingConversionUnits,
      schemaTree?.budget?.conversionUnits ?? [],
    );

    if (!R.isEmpty(creates)) {
      await conversionUnitCreateBulk({
        variables: { input: { schemaId, conversionUnits: creates } },
      });
    }

    if (!R.isEmpty(updates)) {
      await conversionUnitUpdateBulk({
        variables: { input: { schemaId, conversionUnits: updates } },
      });
    }
  };

  const getPendingAttributeCreationPayload = (
    pendingCreationsData: PendingCreations,
    schemaId: any,
  ) => {
    setDisabled(true);
    const currentElement = pendingCreationsData[schemaId];

    const attributes = R.keys(currentElement).reduce((acc, groupKey) => {
      return [...acc, ...R.values(currentElement[groupKey])];
    }, []);

    return attributes.map(attribute =>
      R.omit(['id', 'isTempAttribute'], attribute),
    );
  };

  const handlePublishChanges = async () => {
    setDisabled(true);
    // Each element that has changed attributes must be updated individually
    // This creates an array of promises per updated element.
    const pendingUpdatePromises = R.keys(pendingUpdates).reduce(
      (acc, key) => [
        ...acc,
        publishPendingChanges({
          schemaId: key.toString(),
          attributes: R.values(pendingUpdates[key]).map(
            (attribute): AttributeUpdateBulkInputItem =>
              R.omit(['tempEnumsIndexes'], attribute),
          ),
        }),
      ],
      [],
    );

    // Each changed attribute groups must be updated individually
    // This creates an array of promises per updated group
    const pendingGroupUpdatePromises = R.keys(pendingGroupUpdates).reduce(
      (acc, key) => [
        ...acc,
        publishedPendingGroupChanges({
          schemaId: key.toString(),
          attributeGroups: R.values(pendingGroupUpdates[key]),
        }),
      ],
      [],
    );

    // Each element that has newly created attributes must be updated individually
    // This creates an array of promises per element with new attributes.
    const pendingCreationPromises = R.keys(pendingCreations).reduce(
      (acc, key) => [
        ...acc,
        publishPendingCreations({
          schemaId: key.toString(),
          // @ts-ignore
          attributes: getPendingAttributeCreationPayload(pendingCreations, key),
        }),
      ],
      [],
    );

    // Each element that has reordered attributes/groups must be updated individually
    // This creates an array of promises per element with the updates
    const pendingSchemaUpdatePromises = R.keys(pendingSchemaUpdates).reduce(
      (acc, key) => [
        ...acc,
        publishPendingSchemaChanges({
          schemaId: key.toString(),
          attributeGroups: getAttributeGroupsPayload(pendingSchemaUpdates[key]),
        }),
      ],
      [],
    );

    const pendingConversionUnitPromises = R.isNil(pendingConversionUnits)
      ? []
      : [publishPendingConversionUnits()];

    const cleanup = async () => {
      await refetchSchemaTree();

      setPendingGroupUpdates(null);
      setPendingUpdates(null);
      setPendingCreations(null);
      setPendingSchemaUpdates(null);
      setPendingConversionUnits(null);
      setModalVariant(null);
      setDisabled(false);
    };

    try {
      await Promise.all([
        ...pendingUpdatePromises,
        ...pendingCreationPromises,
        ...pendingGroupUpdatePromises,
        ...pendingSchemaUpdatePromises,
        ...pendingConversionUnitPromises,
      ]);
    } catch (error) {
      // close modal and reset state on error to prevent submit spam
      cleanup();
    }
    cleanup();
  };

  const modalValue = getModalValue(modalVariant);
  const hasPendingChanges =
    !isNilOrEmpty(pendingUpdates) ||
    !isNilOrEmpty(pendingCreations) ||
    !isNilOrEmpty(pendingGroupUpdates) ||
    !isNilOrEmpty(pendingSchemaUpdates) ||
    !R.isNil(pendingConversionUnits);
  const onConfirm = hasPendingChanges
    ? () => handlePublishChanges()
    : () => handleSchemaUpdate();

  const getFooter = () => {
    return modalVariant === ModalVariant.DRAFT_ERROR ? (
      <Button
        color="primary"
        variant="contained"
        onClick={() => setModalVariant(null)}
      >
        {modalValue?.button}
      </Button>
    ) : (
      <div>
        <Button
          onClick={() => setModalVariant(null)}
          style={{ marginRight: '0.5rem' }}
        >
          Cancel
        </Button>
        <Button
          disabled={disabled}
          color="primary"
          variant="contained"
          onClick={onConfirm}
        >
          {modalValue?.button}
        </Button>
      </div>
    );
  };

  const IconComponent = schemaTree.isPublished ? LockIcon : EditIcon;
  const text = schemaTree.isPublished ? 'published' : 'draft';
  const publishedOnClick = schemaTree.isPublished
    ? () => {}
    : () => setModalVariant(ModalVariant.PUBLISH_CONFIRM);
  const draftOnClick = !schemaTree.isPublished
    ? () => {}
    : () => setModalVariant(ModalVariant.DRAFT_CONFIRM);

  return (
    <>
      {hasPendingChanges ? (
        <Button
          style={styles.publishButton}
          variant="contained"
          onClick={() => setModalVariant(ModalVariant.PENDING_PUBLISH_CONFIRM)}
        >
          Publish
        </Button>
      ) : (
        <Menu
          icon={
            <div className={classes.statusContainer}>
              <Icon icon={IconComponent} color={colors.neutral.white} />
              <div className={classes.statusText}>{text}</div>
              <Icon icon={ArrowDropDownIcon} color={colors.neutral.white} />
            </div>
          }
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          noIconButton
        >
          <MenuItem
            key="published"
            onClick={publishedOnClick}
            selected={schemaTree.isPublished}
            startAdornment={<Icon icon={LockIcon} />}
          >
            <div className={classes.statusOption}>
              <div>PUBLISHED</div>
              {schemaTree.isPublished && (
                <Icon icon={CheckIcon} color={colors.brand.blue} />
              )}
            </div>
          </MenuItem>
          <MenuItem
            key="draft"
            onClick={draftOnClick}
            selected={!schemaTree.isPublished}
            startAdornment={<Icon icon={EditIcon} />}
          >
            <div className={classes.statusOption}>
              <div>DRAFT</div>
              {!schemaTree.isPublished && (
                <Icon icon={CheckIcon} color={colors.brand.blue} />
              )}
            </div>
          </MenuItem>
        </Menu>
      )}
      <Modal
        title={modalValue?.title}
        open={!isNilOrEmpty(modalVariant)}
        onCancel={() => setModalVariant(null)}
        footer={getFooter()}
      >
        {modalValue?.content}
      </Modal>
    </>
  );
};

export default SchemaStatus;
